import React from 'react';
import { ProviderSetupGuideLink } from './ProviderSetupGuideModal';
import type { JiraConnectionSettings, UserSettingsUpdate } from '../../services/clients/types';
import { Button } from '../../ui/common/Button';
import { buttonRowStyle, fieldGridStyle, inputStyle } from './settingsStyles';
import { fromDateInputValue, toDateInputValue } from './settingsHelpers';

export interface SettingsJiraAdvancedFormProps {
  open: boolean;
  saving: boolean;
  jiraDraft: JiraConnectionSettings;
  jiraTokenDraft: string;
  tokenConfigured: boolean;
  onJiraDraft: (value: JiraConnectionSettings) => void;
  onJiraTokenDraft: (value: string) => void;
  onAutoSaveJiraConnection: (value: JiraConnectionSettings) => void;
  onSavePatch: (
    patch: UserSettingsUpdate,
    successMessage: string,
    after?: () => void,
  ) => Promise<void>;
}

export const SettingsJiraAdvancedForm: React.FC<SettingsJiraAdvancedFormProps> = ({
  open,
  saving,
  jiraDraft,
  jiraTokenDraft,
  tokenConfigured,
  onJiraDraft,
  onJiraTokenDraft,
  onAutoSaveJiraConnection,
  onSavePatch,
}) => {
  if (!open) return null;
  const hasTokenDraft = Boolean(jiraTokenDraft.trim());
  return (
    <div
      data-testid="settings-jira-advanced"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: '4px 0',
      }}
    >
      <div style={fieldGridStyle}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--z-text-2)' }}>Site URL</span>
          <input
            data-testid="settings-jira-base-url"
            type="url"
            autoComplete="off"
            placeholder="https://tu-org.atlassian.net"
            value={jiraDraft.baseUrl}
            onChange={(e) => onJiraDraft({ ...jiraDraft, baseUrl: e.target.value })}
            onBlur={() => onAutoSaveJiraConnection(jiraDraft)}
            style={inputStyle}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--z-text-2)' }}>Email</span>
          <input
            data-testid="settings-jira-email"
            type="email"
            autoComplete="off"
            placeholder="tu@empresa.com"
            value={jiraDraft.email}
            onChange={(e) => onJiraDraft({ ...jiraDraft, email: e.target.value })}
            onBlur={() => onAutoSaveJiraConnection(jiraDraft)}
            style={inputStyle}
          />
        </label>
      </div>
      <input
        data-testid="settings-jira-token"
        type="password"
        autoComplete="off"
        placeholder={
          tokenConfigured
            ? 'Pega un nuevo API token de Atlassian para reemplazarlo'
            : 'Pega tu API token de Atlassian para conectar la cuenta'
        }
        value={jiraTokenDraft}
        onChange={(e) => onJiraTokenDraft(e.target.value)}
        style={inputStyle}
      />
      <ProviderSetupGuideLink source="jira" />
      <label style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 260 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--z-text-2)' }}>
          El token vence el (opcional)
        </span>
        <input
          data-testid="settings-jira-token-expires"
          type="date"
          value={toDateInputValue(jiraDraft.apiTokenExpiresAt)}
          onChange={(e) => {
            const apiTokenExpiresAt = fromDateInputValue(e.target.value);
            onJiraDraft({ ...jiraDraft, apiTokenExpiresAt });
            void onSavePatch(
              { jira: { apiTokenExpiresAt } },
              apiTokenExpiresAt ? 'Fecha de vencimiento guardada' : 'Fecha de vencimiento quitada',
            );
          }}
          style={inputStyle}
        />
        <span style={{ fontSize: 11.5, color: 'var(--z-dim)' }}>
          Atlassian no expone la caducidad por API; anótala para que Zunelo te avise 7 días antes.
        </span>
      </label>
      <div style={buttonRowStyle}>
        {hasTokenDraft ? (
          <Button
            size="sm"
            variant="primary"
            data-testid="settings-jira-save"
            disabled={saving}
            loading={saving}
            onClick={() =>
              void onSavePatch(
                {
                  jiraApiToken: jiraTokenDraft.trim(),
                  jira: { ...jiraDraft, authMethod: 'api_token' },
                },
                'Token de Jira actualizado',
                () => onJiraTokenDraft(''),
              )
            }
          >
            Guardar token
          </Button>
        ) : null}
        {tokenConfigured ? (
          <Button
            size="sm"
            variant="ghost"
            data-testid="settings-jira-clear"
            disabled={saving}
            onClick={() => void onSavePatch({ clearJiraApiToken: true }, 'Token de Jira eliminado')}
          >
            Eliminar token
          </Button>
        ) : null}
      </div>
    </div>
  );
};
